import { useCallback, useMemo, useState } from 'react';
import { apiClient } from '@/services/api';
import { FormField, AssetUploadField, Skeleton } from '@connor-music/shared';
import type { Project, ProjectBranding } from '@shared/types';
import { AlertCircle, CheckCircle, Save, Loader2, RotateCcw } from 'lucide-react';

interface ProjectBrandingConfigProps {
  project: Project;
  onUpdate: () => void;
}

/** Map of asset_type to the URL key in ProjectBranding */
const ASSET_URL_KEYS: Record<string, keyof ProjectBranding> = {
  logo_light: 'logo_light_url',
  logo_dark: 'logo_dark_url',
  logo_mark_light: 'logo_mark_light_url',
  logo_mark_dark: 'logo_mark_dark_url',
  favicon: 'favicon_url',
};

export function ProjectBrandingConfig({ project, onUpdate }: ProjectBrandingConfigProps) {
  const defaults = useMemo(() => project.branding_defaults ?? {}, [project.branding_defaults]);
  const overrides = useMemo(() => project.branding_overrides ?? {}, [project.branding_overrides]);

  // Compute effective branding (defaults merged with overrides)
  const effective = useMemo<ProjectBranding>(
    () => ({ ...defaults, ...overrides }),
    [defaults, overrides],
  );

  // Local state for text/numeric fields (only saved on submit)
  const [portalName, setPortalName] = useState(overrides.portal_name ?? '');
  const [termsUrl, setTermsUrl] = useState(overrides.terms_url ?? '');
  const [privacyUrl, setPrivacyUrl] = useState(overrides.privacy_url ?? '');
  const [logoWidth, setLogoWidth] = useState<number | ''>(overrides.logo_width ?? '');
  const [loginLogoWidth, setLoginLogoWidth] = useState<number | ''>(overrides.login_logo_width ?? '');

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const inputClasses =
    'block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-primary-400 dark:focus:ring-primary-400';

  // Check if a text field has been overridden
  const isTextOverridden = useCallback((field: keyof ProjectBranding) => {
    return overrides[field] !== undefined;
  }, [overrides]);

  // Upload handler for a specific asset type
  const handleAssetUpload = useCallback(async (assetType: string, file: File): Promise<string> => {
    // 1. Get presigned upload URL
    const { upload_url, s3_key } = await apiClient.getBrandingUploadUrl(
      project.id, assetType, file.type,
    );

    // 2. Upload file directly to S3
    await fetch(upload_url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });

    // 3. Save the S3 key to project branding
    const keyField = `${assetType}_key`;
    const updatedBranding = await apiClient.updateProjectBranding(project.id, {
      [keyField]: s3_key,
    });

    // 4. Refresh project data
    onUpdate();

    // Return the URL for immediate preview
    const urlKey = ASSET_URL_KEYS[assetType];
    return urlKey ? (updatedBranding[urlKey] as string) ?? '' : '';
  }, [project.id, onUpdate]);

  // Remove handler for a specific asset type
  const handleAssetRemove = useCallback(async (assetType: string) => {
    await apiClient.deleteProjectBrandingAsset(project.id, assetType);
    onUpdate();
  }, [project.id, onUpdate]);

  // Save text/numeric field overrides
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsSaving(true);

    try {
      const updates: Record<string, string | number | undefined> = {};

      // Only include fields that have values (overrides)
      if (portalName) updates.portal_name = portalName;
      if (termsUrl) updates.terms_url = termsUrl;
      if (privacyUrl) updates.privacy_url = privacyUrl;
      if (logoWidth !== '') updates.logo_width = logoWidth;
      if (loginLogoWidth !== '') updates.login_logo_width = loginLogoWidth;

      if (Object.keys(updates).length > 0) {
        await apiClient.updateProjectBranding(project.id, updates);
      }

      setSuccessMessage('Branding settings saved.');
      onUpdate();
    } catch {
      setError('Failed to save branding settings.');
    } finally {
      setIsSaving(false);
    }
  };

  // Reset a text/numeric field to default
  const handleResetField = useCallback(async (field: string) => {
    try {
      // Remove the field from branding overrides via the branding update
      // by sending an update that removes it — we use the delete endpoint for assets
      // For text fields, we need to remove from the branding map
      // Currently the API doesn't have a per-text-field delete, so we'll handle this
      // by updating the field to empty, then the backend can handle cleanup
      // Actually, let's use a proper approach: PATCH with null-like value
      // For now, clear local state and save
      if (field === 'portal_name') setPortalName('');
      if (field === 'terms_url') setTermsUrl('');
      if (field === 'privacy_url') setPrivacyUrl('');
      if (field === 'logo_width') setLogoWidth('');
      if (field === 'login_logo_width') setLoginLogoWidth('');
    } catch {
      setError('Failed to reset field.');
    }
  }, []);

  return (
    <div>
      {/* Messages */}
      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-400">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {successMessage && (
        <div className="mb-4 flex items-start gap-2 rounded-lg bg-green-50 dark:bg-green-900/20 p-4 text-sm text-green-700 dark:text-green-400">
          <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>{successMessage}</span>
        </div>
      )}

      <form onSubmit={handleSave}>
        {/* General Settings */}
        <div className="rounded-lg border bg-white p-6 dark:border-gray-700 dark:bg-gray-800 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            General
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Override the global portal settings for this project. Leave blank to use the global default.
          </p>
          <div className="space-y-4">
            <FormField label="Portal Name" htmlFor="branding-portal-name">
              <div className="flex gap-2">
                <input
                  id="branding-portal-name"
                  type="text"
                  value={portalName}
                  onChange={(e) => setPortalName(e.target.value)}
                  className={inputClasses}
                  placeholder={defaults.portal_name || 'Connor Music Audit Portal'}
                />
                {isTextOverridden('portal_name') && (
                  <button
                    type="button"
                    onClick={() => handleResetField('portal_name')}
                    className="flex-shrink-0 p-2 text-gray-400 hover:text-red-500 transition-colors"
                    title="Reset to default"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                )}
              </div>
            </FormField>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Terms of Use URL" htmlFor="branding-terms-url">
                <input
                  id="branding-terms-url"
                  type="url"
                  value={termsUrl}
                  onChange={(e) => setTermsUrl(e.target.value)}
                  className={inputClasses}
                  placeholder={defaults.terms_url || 'https://example.com/terms'}
                />
              </FormField>
              <FormField label="Privacy Policy URL" htmlFor="branding-privacy-url">
                <input
                  id="branding-privacy-url"
                  type="url"
                  value={privacyUrl}
                  onChange={(e) => setPrivacyUrl(e.target.value)}
                  className={inputClasses}
                  placeholder={defaults.privacy_url || 'https://example.com/privacy'}
                />
              </FormField>
            </div>
          </div>
        </div>

        {/* Logo Assets */}
        <div className="rounded-lg border bg-white p-6 dark:border-gray-700 dark:bg-gray-800 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            Logos
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Upload project-specific logo assets. If not set, the global default will be used.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <AssetUploadField
              label="Logo (Light Theme)"
              hasDefault={!!defaults.logo_light_url}
              isOverridden={!!overrides.logo_light_url}
              onUpload={(file) => handleAssetUpload('logo_light', file)}
              onRemove={() => handleAssetRemove('logo_light')}
            />
            <AssetUploadField
              label="Logo (Dark Theme)"
              hasDefault={!!defaults.logo_dark_url}
              isOverridden={!!overrides.logo_dark_url}
              onUpload={(file) => handleAssetUpload('logo_dark', file)}
              onRemove={() => handleAssetRemove('logo_dark')}
            />
            <AssetUploadField
              label="Logo Mark (Light Theme)"
              hasDefault={!!defaults.logo_mark_light_url}
              isOverridden={!!overrides.logo_mark_light_url}
              onUpload={(file) => handleAssetUpload('logo_mark_light', file)}
              onRemove={() => handleAssetRemove('logo_mark_light')}
            />
            <AssetUploadField
              label="Logo Mark (Dark Theme)"
              hasDefault={!!defaults.logo_mark_dark_url}
              isOverridden={!!overrides.logo_mark_dark_url}
              onUpload={(file) => handleAssetUpload('logo_mark_dark', file)}
              onRemove={() => handleAssetRemove('logo_mark_dark')}
            />
            <AssetUploadField
              label="Favicon"
              hasDefault={!!defaults.favicon_url}
              isOverridden={!!overrides.favicon_url}
              accept="image/x-icon,image/vnd.microsoft.icon,image/png,image/svg+xml"
              onUpload={(file) => handleAssetUpload('favicon', file)}
              onRemove={() => handleAssetRemove('favicon')}
            />
          </div>
        </div>

        {/* Dimensions */}
        <div className="rounded-lg border bg-white p-6 dark:border-gray-700 dark:bg-gray-800 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Dimensions
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Logo Width (px)" htmlFor="branding-logo-width">
              <input
                id="branding-logo-width"
                type="number"
                min={40}
                max={400}
                value={logoWidth}
                onChange={(e) => setLogoWidth(e.target.value ? parseInt(e.target.value, 10) : '')}
                className={inputClasses}
                placeholder={String(defaults.logo_width || 120)}
              />
            </FormField>
            <FormField label="Login Logo Width (px)" htmlFor="branding-login-logo-width">
              <input
                id="branding-login-logo-width"
                type="number"
                min={40}
                max={600}
                value={loginLogoWidth}
                onChange={(e) => setLoginLogoWidth(e.target.value ? parseInt(e.target.value, 10) : '')}
                className={inputClasses}
                placeholder={String(defaults.login_logo_width || 200)}
              />
            </FormField>
          </div>
        </div>

        {/* Preview */}
        {(effective.logo_light_url || effective.logo_dark_url || effective.logo_mark_light_url || effective.logo_mark_dark_url) && (
          <div className="rounded-lg border bg-white p-6 dark:border-gray-700 dark:bg-gray-800 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Preview
            </h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {effective.logo_light_url && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    Logo (Light)
                  </p>
                  <div className="rounded-md bg-white border border-gray-200 p-4 dark:border-gray-600">
                    <img
                      src={effective.logo_light_url}
                      alt="Logo light preview"
                      style={{ width: effective.logo_width || 120 }}
                      className="h-auto"
                    />
                  </div>
                </div>
              )}
              {effective.logo_dark_url && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    Logo (Dark)
                  </p>
                  <div className="rounded-md bg-gray-900 border border-gray-700 p-4">
                    <img
                      src={effective.logo_dark_url}
                      alt="Logo dark preview"
                      style={{ width: effective.logo_width || 120 }}
                      className="h-auto"
                    />
                  </div>
                </div>
              )}
              {effective.logo_mark_light_url && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    Mark (Light)
                  </p>
                  <div className="rounded-md bg-white border border-gray-200 p-4 dark:border-gray-600">
                    <img src={effective.logo_mark_light_url} alt="Mark light preview" className="h-8 w-8" />
                  </div>
                </div>
              )}
              {effective.logo_mark_dark_url && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    Mark (Dark)
                  </p>
                  <div className="rounded-md bg-gray-900 border border-gray-700 p-4">
                    <img src={effective.logo_mark_dark_url} alt="Mark dark preview" className="h-8 w-8" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Login Screen Preview */}
        {(effective.logo_light_url || effective.logo_dark_url) && (
          <div className="rounded-lg border bg-white p-6 dark:border-gray-700 dark:bg-gray-800 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Login Screen Preview
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Preview of how the project branding appears on the employee and customer login pages.
            </p>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Employee Login Preview */}
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Employee Login
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* Light theme */}
                  {effective.logo_light_url && (
                    <div className="rounded-lg bg-gray-50 p-4 dark:border dark:border-gray-600">
                      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="text-center space-y-2">
                          <div className="flex justify-center">
                            <img
                              src={effective.logo_light_url}
                              alt="Employee login light preview"
                              style={{ width: Math.min(effective.login_logo_width || 200, 140) }}
                              className="h-auto"
                            />
                          </div>
                          <p className="text-[10px] font-bold text-gray-900 truncate">
                            {effective.portal_name || 'Connor Music Audit'}
                          </p>
                          <p className="text-[8px] text-gray-500">Employee Portal</p>
                          <div className="rounded bg-gray-50 px-2 py-1">
                            <p className="text-[7px] text-gray-500">Authorized employees only</p>
                          </div>
                          <div className="rounded bg-accent-600 px-2 py-1">
                            <span className="text-[8px] font-medium text-white">Sign in with Microsoft Entra</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 flex justify-center gap-2">
                        <span className="text-[7px] text-gray-400">Terms</span>
                        <span className="text-[7px] text-gray-300">&middot;</span>
                        <span className="text-[7px] text-gray-400">Privacy</span>
                      </div>
                    </div>
                  )}
                  {/* Dark theme */}
                  {effective.logo_dark_url && (
                    <div className="rounded-lg bg-gray-900 p-4 border border-gray-700">
                      <div className="rounded-lg border border-gray-700 bg-gray-800 p-4 shadow-sm">
                        <div className="text-center space-y-2">
                          <div className="flex justify-center">
                            <img
                              src={effective.logo_dark_url}
                              alt="Employee login dark preview"
                              style={{ width: Math.min(effective.login_logo_width || 200, 140) }}
                              className="h-auto"
                            />
                          </div>
                          <p className="text-[10px] font-bold text-white truncate">
                            {effective.portal_name || 'Connor Music Audit'}
                          </p>
                          <p className="text-[8px] text-gray-400">Employee Portal</p>
                          <div className="rounded bg-gray-700/50 px-2 py-1">
                            <p className="text-[7px] text-gray-300">Authorized employees only</p>
                          </div>
                          <div className="rounded bg-accent-600 px-2 py-1">
                            <span className="text-[8px] font-medium text-white">Sign in with Microsoft Entra</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 flex justify-center gap-2">
                        <span className="text-[7px] text-gray-500">Terms</span>
                        <span className="text-[7px] text-gray-600">&middot;</span>
                        <span className="text-[7px] text-gray-500">Privacy</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Login Preview */}
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Customer Login
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* Light theme */}
                  {effective.logo_light_url && (
                    <div className="rounded-lg bg-gray-50 p-4 dark:border dark:border-gray-600">
                      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="text-center space-y-2">
                          <div className="flex justify-center">
                            <img
                              src={effective.logo_light_url}
                              alt="Customer login light preview"
                              style={{ width: Math.min(effective.login_logo_width || 200, 140) }}
                              className="h-auto"
                            />
                          </div>
                          <p className="text-[10px] font-bold text-gray-900 truncate">
                            {effective.portal_name || 'Connor Music Audit'}
                          </p>
                          <p className="text-[8px] text-gray-500">Customer Portal</p>
                          <div className="my-2 border-t border-gray-200" />
                          <div className="rounded bg-gray-50 px-2 py-1">
                            <span className="text-[7px] text-gray-600">Authorized audit participants only.</span>
                          </div>
                          <div className="rounded border border-gray-300 bg-white px-2 py-1">
                            <span className="text-[8px] text-gray-400">you@company.com</span>
                          </div>
                          <div className="rounded bg-accent-600 px-2 py-1">
                            <span className="text-[8px] font-medium text-white">Send Login Code</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 flex justify-center gap-2">
                        <span className="text-[7px] text-gray-400">Terms</span>
                        <span className="text-[7px] text-gray-300">&middot;</span>
                        <span className="text-[7px] text-gray-400">Privacy</span>
                      </div>
                    </div>
                  )}
                  {/* Dark theme */}
                  {effective.logo_dark_url && (
                    <div className="rounded-lg bg-gray-900 p-4 border border-gray-700">
                      <div className="rounded-lg border border-gray-700 bg-gray-800 p-4 shadow-sm">
                        <div className="text-center space-y-2">
                          <div className="flex justify-center">
                            <img
                              src={effective.logo_dark_url}
                              alt="Customer login dark preview"
                              style={{ width: Math.min(effective.login_logo_width || 200, 140) }}
                              className="h-auto"
                            />
                          </div>
                          <p className="text-[10px] font-bold text-white truncate">
                            {effective.portal_name || 'Connor Music Audit'}
                          </p>
                          <p className="text-[8px] text-gray-400">Customer Portal</p>
                          <div className="my-2 border-t border-gray-700" />
                          <div className="rounded bg-gray-700/50 px-2 py-1">
                            <span className="text-[7px] text-gray-300">Authorized audit participants only.</span>
                          </div>
                          <div className="rounded border border-gray-600 bg-gray-800 px-2 py-1">
                            <span className="text-[8px] text-gray-500">you@company.com</span>
                          </div>
                          <div className="rounded bg-accent-600 px-2 py-1">
                            <span className="text-[8px] font-medium text-white">Send Login Code</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 flex justify-center gap-2">
                        <span className="text-[7px] text-gray-500">Terms</span>
                        <span className="text-[7px] text-gray-600">&middot;</span>
                        <span className="text-[7px] text-gray-500">Privacy</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50 dark:bg-primary-500 dark:hover:bg-primary-600"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}

export function ProjectBrandingConfigSkeleton() {
  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 space-y-4">
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-3 w-64" />
        <div className="space-y-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-9 w-full rounded-md" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-9 w-full rounded-md" />
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 space-y-4">
        <Skeleton className="h-5 w-16" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      </div>
    </div>
  );
}
